import { calculateFinancials } from './financialCalculations';
import { formatCurrency, formatPercentage } from '../utils/formatters';

/**
 * Goal Seek Service for Solar Financial Model
 * Finds the required own funds to achieve a target IRR using binary search
 */

export const findRequiredOwnFunds = (inputs, targetIRR, setIsLoading, setGoalSeekMessage, setInputs) => {
    if (setIsLoading) setIsLoading(true);
    if (setGoalSeekMessage) setGoalSeekMessage('');
    
    return new Promise((resolve) => {
        setTimeout(() => {
            try {
                const result = performGoalSeek(inputs, targetIRR);
                
                if (result.success) {
                    if (setInputs) {
                        setInputs(prev => ({ ...prev, ownFunds: result.ownFunds }));
                    }
                    if (setGoalSeekMessage) {
                        setGoalSeekMessage(`Solution found for Own Funds: ${formatCurrency(result.ownFunds)}`);
                    }
                    resolve({ success: true, ownFunds: result.ownFunds });
                } else {
                    if (setGoalSeekMessage) {
                        setGoalSeekMessage(result.message);
                    }
                    resolve({ success: false, message: result.message });
                }
            } catch (error) {
                console.error('Goal seek error:', error);
                const errorMessage = 'Error during goal seek calculation';
                if (setGoalSeekMessage) {
                    setGoalSeekMessage(errorMessage);
                }
                resolve({ success: false, message: errorMessage });
            } finally {
                if (setIsLoading) setIsLoading(false);
            }
        }, 100);
    });
};

const performGoalSeek = (inputs, targetIRR) => {
    const tolerance = 1e-5;
    const maxIterations = 100;
    
    const getIrrForFunds = (funds) => {
        if (funds <= 0) return -1;
        try {
            const result = calculateFinancials({ ...inputs, ownFunds: funds });
            const irr = result?.indicators?.projectIRR;
            return (typeof irr === 'number' && isFinite(irr)) ? irr : -1;
        } catch (error) {
            console.error('Error calculating IRR for funds:', funds, error);
            return -1;
        }
    };

    // Set initial bounds
    let lowFunds = 1;
    let highFunds = (inputs.installedCapacity * inputs.investmentPerMW) * 1.1;
    
    // Check if target is achievable
    const irrAtLow = getIrrForFunds(lowFunds);
    const irrAtHigh = getIrrForFunds(highFunds);
    
    if (targetIRR < irrAtLow || targetIRR > irrAtHigh) {
        return {
            success: false,
            message: `Target IRR (${formatPercentage(targetIRR)}) is unachievable. Must be between ${formatPercentage(irrAtLow)} and ${formatPercentage(irrAtHigh)}.`
        };
    }
    
    // Binary search for the solution
    let midFunds = lowFunds;
    for (let i = 0; i < maxIterations; i++) {
        midFunds = lowFunds + (highFunds - lowFunds) / 2;
        
        // Check for convergence
        if (Math.abs(midFunds - highFunds) < 0.01 || Math.abs(midFunds - lowFunds) < 0.01) {
            break;
        }
        
        const irrAtMid = getIrrForFunds(midFunds);
        
        // Check if we found the target
        if (Math.abs(irrAtMid - targetIRR) < tolerance) {
            break;
        }
        
        // Adjust bounds
        if (irrAtMid < targetIRR) {
            lowFunds = midFunds;
        } else {
            highFunds = midFunds;
        }
    }
    
    return {
        success: true,
        ownFunds: midFunds
    };
};

/**
 * Validates goal seek parameters
 */
export const validateGoalSeekInputs = (inputs, targetIRR) => {
    if (!inputs || typeof inputs !== 'object') {
        return { valid: false, message: 'Invalid inputs provided' };
    }
    
    if (typeof targetIRR !== 'number' || !isFinite(targetIRR) || targetIRR <= 0) {
        return { valid: false, message: 'Target IRR must be a positive number' };
    }
    
    if (targetIRR > 1) {
        return { valid: false, message: 'Target IRR seems too high (should be decimal, e.g., 0.12 for 12%)' };
    }
    
    if (!inputs.installedCapacity || !inputs.investmentPerMW) {
        return { valid: false, message: 'Project capacity and investment parameters are required' };
    }
    
    return { valid: true };
};

/**
 * Calculates IRR sensitivity around current own funds
 */
export const calculateIrrSensitivity = (inputs, fundVariations = [-0.2, -0.1, 0, 0.1, 0.2]) => {
    const baseFunds = inputs.ownFunds;
    const results = [];
    
    fundVariations.forEach(variation => {
        const testFunds = baseFunds * (1 + variation);
        try {
            const result = calculateFinancials({ ...inputs, ownFunds: testFunds });
            results.push({
                variation: variation,
                ownFunds: testFunds,
                projectIRR: result?.indicators?.projectIRR || 0,
                capitalIRR: result?.indicators?.capitalIRR || 0
            });
        } catch (error) {
            results.push({
                variation: variation,
                ownFunds: testFunds,
                projectIRR: 0,
                capitalIRR: 0,
                error: error.message
            });
        }
    });
    
    return results;
}; 